import {
  ArrowDownIcon,
  FaroIconButton,
  FaroText,
  grey,
} from "@faro-lotv/flat-ui";
import { Box, Paper, Stack } from "@mui/material";
import { useState, type JSX } from "react";
import { WizardNavigation } from "./use-wizard-navigation";
import { WizardFooter } from "./wizard-footer";

/**
 * @param navigation The navigation state and actions of the wizard
 * @returns the step guidance for small screens, docked below the 3D view
 */
export function WizardBottomSheet(navigation: WizardNavigation): JSX.Element {
  const { activeStep, activeIndex, steps } = navigation;
  const [isExpanded, setIsExpanded] = useState(!activeStep.isCanvasFocused);
  const hasDetails = !!activeStep.instructions || !!activeStep.Panel;

  return (
    <Paper
      elevation={8}
      aria-label="workflow step"
      // The sheet never takes more than most of the screen, and only its content scrolls:
      // the step actions at the bottom always stay visible, whatever the screen height
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 1,
        minHeight: 0,
        maxHeight: "75%",
        position: "relative",
        zIndex: 2,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        overflow: "hidden",
        pb: "env(safe-area-inset-bottom)",
      }}
    >
      <Stack sx={{ px: 2, pt: 1, pb: 1.5, minHeight: 0, flexShrink: 1 }}>
        <Stack
          direction="row"
          sx={{ alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
            <FaroText variant="bodyS" color={grey[600]}>
              {`Step ${activeIndex + 1} of ${steps.length}`}
            </FaroText>
            <FaroText variant="heading16" shouldElide>
              {activeStep.label}
            </FaroText>
          </Stack>
          {hasDetails && (
            <FaroIconButton
              aria-label={isExpanded ? "collapse step" : "expand step"}
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{ width: 48, height: 48 }}
            >
              <ArrowDownIcon
                sx={{
                  transform: isExpanded ? undefined : "rotate(180deg)",
                  transition: "transform 150ms",
                }}
              />
            </FaroIconButton>
          )}
        </Stack>
        {isExpanded && (
          <Box
            component="div"
            sx={{
              maxHeight: "40dvh",
              minHeight: 0,
              flexShrink: 1,
              overflow: "auto",
              pt: 1,
            }}
          >
            <Stack sx={{ gap: 2 }}>
              <FaroText variant="bodyM" color={grey[700]}>
                {activeStep.instructions}
              </FaroText>
              {activeStep.Panel && <activeStep.Panel />}
            </Stack>
          </Box>
        )}
        <Box component="div" sx={{ flexShrink: 0 }}>
          <WizardFooter {...navigation} />
        </Box>
      </Stack>
    </Paper>
  );
}
